import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import { Save as SaveIcon } from 'lucide-react';

import { noteStatus, noteSeverity, noteType } from '../../utils/selectOptions';

const CREATE_NOTE = gql`
	mutation CreateNote($noteobj: String, $noteobjrid: String, $status: String, $severity: String, $notetype: String, $title: String, $content: String) {
		createNote(noteobj: $noteobj, noteobjrid: $noteobjrid, status: $status, severity: $severity, notetype: $notetype, title: $title, content: $content){
            responseCode
            responseText
            actionType
            objectType
            objectId
		}
	}
`;

const scrollToTop = () =>{window.scrollTo({top: 0, behavior: 'smooth'});};

export default function CreateForm(props) {
    const { noteobj, noteobjrid, navGoBack, navToUpdate } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [serverErr, setServerErr] = useState(false);

    const [errCode, errCodeSet] = useState(false);
    const [severityErr, severityErrSet] = useState(false);
    const [statusErr, statusErrSet] = useState(false);
    const [notetypeErr, notetypeErrSet] = useState(false);
    const [titleErr, titleErrSet] = useState(false);

    const [severity, companySet] = useState('');
    const handleChangeseverity = (event) => {
        companySet(event.target.value);
    };

    const [status, statusSet] = useState('');
    const handleChangestatus = (event) => {
        statusSet(event.target.value);
    };

    const [notetype, notetypeSet] = useState('');
    const handleChangenotetype = (event) => {
        notetypeSet(event.target.value);
    };

    const [title, titleSet] = useState('');
    const handleChangetitle = (event) => {
        titleSet(event.target.value);
    };

    const [content, contentSet] = useState('');
    const handleChangecontent = (event) => {
        contentSet(event.target.value);
    };

    const collectFormData = () => {
        const formData = {};
        let formValid = true;

        errCodeSet(false);
        setServerErr(false);
        severityErrSet(false);
        statusErrSet(false);
        notetypeErrSet(false);
        titleErrSet(false);

        if (!severity || severity === ""){
            severityErrSet(true);
            formValid = false;
        }

        if (!status || status === ""){
            statusErrSet(true);
            formValid = false;
        }

        if (!notetype || notetype === ""){
            notetypeErrSet(true);
            formValid = false;
        }

        if (!title || title === ""){
            titleErrSet(true);
            formValid = false;
        }

        if (formValid){
            setServerErr(false);
            formData.noteobj = noteobj;
            formData.noteobjrid = noteobjrid;
            formData.status = status;
            formData.severity = severity;
            formData.notetype = notetype;
            formData.title = title;
            formData.content = content;

            setIsLoading(true);
            createNote({variables:{...formData}});
        }
        else{
            scrollToTop();
            if(!errCode){
                errCodeSet(true);
            }
        }
        
    }

    const [createNote] = useMutation(CREATE_NOTE, {
        onCompleted: (setData) => {
            setIsLoading(false);
            if (setData && setData?.createNote?.responseCode === "200"){
                const rid = setData?.createNote?.objectId;
                navToUpdate();
            }
            else{
                setServerErr(`There was an issue with your request.`);
                scrollToTop();
            }
        },
        onError: (setError) => {
            setIsLoading(false);
            setServerErr(`There was an error processing your request.`);
            scrollToTop();
        }
    });

    return (
        <Container maxWidth="sm">            
            {isLoading && 
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}><CircularProgress sx={{color:'#c9fb00'}} thickness={1} size={60} /></Backdrop>
            }

            {serverErr &&
            <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ marginTop: '12px', marginBottom: '12px'}}>
                <Grid item>
                    <Alert severity="error" variant="outlined">
                        <AlertTitle>Error</AlertTitle>
                        {serverErr}
                    </Alert>
                </Grid>
            </Grid>
            }

            {errCode &&
            <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ marginTop: '12px', marginBottom: '12px'}}>
                <Grid item>
                    <Alert severity="error" variant="outlined">
                        <AlertTitle>Error</AlertTitle>
                        Please resolve the form errors below!
                    </Alert>
                </Grid>
            </Grid>
            }

            <Paper sx={{ width: '100%', padding: '20px', marginBottom: '3rem' }}>
                <Box component="form" sx={{ '& > :not(style)': { padding: '20px', paddingBottom: '30px' } }}>

                    <TextField variant="standard" id="status" label="Status" error={statusErr} value={status} onChange={handleChangestatus} select fullWidth>
                        {noteStatus.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                    </TextField>

                    <TextField variant="standard" id="severity" label="Severity" error={severityErr} value={severity} onChange={handleChangeseverity} select fullWidth>
                        {noteSeverity.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                    </TextField>

                    <TextField variant="standard" id="notetype" label="Type" error={notetypeErr} value={notetype} onChange={handleChangenotetype} select fullWidth>
                        {noteType.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                    </TextField>

                    <TextField variant="standard" id="title" label="Title" error={titleErr} value={title} onChange={handleChangetitle} fullWidth />

                    <TextField variant="standard" id="content" label="Content" value={content} onChange={handleChangecontent} fullWidth multiline rows={4} />

                </Box>

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{marginTop: '20px'}}>
                    <Grid item>
                        <Button aria-label="cancel" variant="outlined" onClick={navGoBack}>Cancel</Button>
                    </Grid>
                    <Grid item>
                        <Button aria-label="save form" variant="outlined" onClick={collectFormData} endIcon={<SaveIcon strokeWidth={1} size={16} />}>Create Note</Button>
                    </Grid>
                </Grid>

            </Paper>

        </Container>
    );
}